import { apiClient, getApiUrl } from '../lib/apiClient';
import type { 
  Notification,
  NotificationPreferences,
  ListQueryParams,
  Pagination
} from '../types/api';
import { 
  extractListData, 
  extractSingleData, 
  extractPagination,
  type RailsWrappedListResponse,
  type RailsSingleWrappedResponse 
} from '../utils/railsResponseHandler';

// Notification list response based on OpenAPI
interface NotificationListResponse {
  notifications: Notification[];
  pagination: Pagination;
  unread_count: number;
}

// Success response for notification actions
interface NotificationActionResponse {
  success: boolean;
  message: string;
}

// Extended Rails response to include unread_count in meta
interface NotificationRailsResponse extends Omit<RailsWrappedListResponse<Notification>, 'meta'> {
  meta: {
    [key: string]: any;
    pagination: Pagination;
    total_count: number;
    unread_count: number;
  };
}

// Get all notifications
export const getNotifications = async (
  params?: ListQueryParams & {
    unread?: boolean;
    type?: string;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
  }
): Promise<NotificationListResponse> => {
  const response = await apiClient.get<NotificationRailsResponse>(
    getApiUrl('/notifications'),
    { params }
  );
  
  const notifications = extractListData<Notification>(response.data);
  const pagination = extractPagination(response.data) || {
    current_page: 1,
    total_pages: 1,
    total_count: notifications.length,
    per_page: 20,
    has_previous: false,
    has_next: false
  };
  
  return {
    notifications,
    pagination,
    unread_count: (response.data.meta && response.data.meta.unread_count) || 0
  };
};

// Get a specific notification
export const getNotification = async (
  id: string
): Promise<Notification> => {
  const response = await apiClient.get<RailsSingleWrappedResponse<Notification>>(
    getApiUrl(`/notifications/${id}`)
  );
  return extractSingleData<Notification>(response.data);
};

// Delete a notification
export const deleteNotification = async (
  id: string
): Promise<NotificationActionResponse> => {
  const response = await apiClient.delete<RailsSingleWrappedResponse<NotificationActionResponse>>(
    getApiUrl(`/notifications/${id}`)
  );
  return extractSingleData<NotificationActionResponse>(response.data);
};

// Mark a notification as read
export const markNotificationAsRead = async (
  id: string
): Promise<NotificationActionResponse> => {
  const response = await apiClient.post<RailsSingleWrappedResponse<NotificationActionResponse>>(
    getApiUrl(`/notifications/${id}/mark_as_read`)
  );
  return extractSingleData<NotificationActionResponse>(response.data);
};

// Mark all notifications as read
export const markAllNotificationsAsRead = async (): Promise<NotificationActionResponse> => {
  const response = await apiClient.post<RailsSingleWrappedResponse<NotificationActionResponse>>(
    getApiUrl('/notifications/mark_all_as_read')
  );
  return extractSingleData<NotificationActionResponse>(response.data);
};

// Clear all notifications
export const clearAllNotifications = async (): Promise<NotificationActionResponse> => {
  const response = await apiClient.delete<RailsSingleWrappedResponse<NotificationActionResponse>>(
    getApiUrl('/notifications/clear_all')
  );
  return extractSingleData<NotificationActionResponse>(response.data);
};

// Get notification preferences
export const getNotificationPreferences = async (): Promise<NotificationPreferences> => {
  const response = await apiClient.get<RailsSingleWrappedResponse<NotificationPreferences>>(
    getApiUrl('/notifications/preferences')
  );
  return extractSingleData<NotificationPreferences>(response.data);
};

// Update notification preferences
export const updateNotificationPreferences = async (
  preferences: Partial<NotificationPreferences>
): Promise<NotificationPreferences> => {
  const response = await apiClient.patch<RailsSingleWrappedResponse<NotificationPreferences>>(
    getApiUrl('/notifications/preferences'),
    preferences
  );
  return extractSingleData<NotificationPreferences>(response.data);
};